import { useState, useEffect } from "react";
import { trpc } from "../lib/trpc";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const PLATAFORMAS = [
  { value: "mercadolivre", label: "Mercado Livre" },
];

function statusBadge(status: string) {
  if (status === "connected") return <Badge className="bg-green-100 text-green-800">Conectado</Badge>;
  if (status === "error") return <Badge className="bg-red-100 text-red-800">Erro</Badge>;
  if (status === "disabled") return <Badge className="bg-gray-100 text-gray-600">Desativado</Badge>;
  return <Badge className="bg-yellow-100 text-yellow-800">Pendente</Badge>;
}

export default function Integracoes() {
  const { user } = useGoogleAuth();
  const isAdmin = user?.role === "admin";
  const utils = trpc.useUtils();

  const [emitenteIdFiltro, setEmitenteIdFiltro] = useState<number | undefined>(undefined);
  const [modalNovaAberto, setModalNovaAberto] = useState(false);
  const [novaPlataforma, setNovaPlataforma] = useState("mercadolivre");
  const [novaEmitenteId, setNovaEmitenteId] = useState<string>("");


  const { data: emitentes } = trpc.emitentes.list.useQuery(undefined, { enabled: isAdmin });
  const { data: integracoes, refetch } = trpc.integracoes.list.useQuery(
    { emitenteId: emitenteIdFiltro },
    { refetchInterval: 30000 }
  );

  const createMutation = trpc.integracoes.create.useMutation({
    onSuccess: () => {
      toast("Integração criada. Clique em Conectar para autorizar.");
      setModalNovaAberto(false);
      setNovaEmitenteId("");
      refetch();
    },
    onError: (e) => toast(`Erro ao criar integração: ${e.message}`),
  });

  const deleteMutation = trpc.integracoes.delete.useMutation({
    onSuccess: () => {
      toast("Integração desativada.");
      refetch();
    },
    onError: (e) => toast(`Erro ao desativar: ${e.message}`),
  });

  const handleCallbackMutation = trpc.integracoes.handleCallback.useMutation({
    onSuccess: () => {
      toast("Integração conectada com sucesso!");
      refetch();
    },
    onError: (e) => toast(`Erro ao conectar: ${e.message}`),
  });

  // Detectar callback OAuth na URL e processar automaticamente
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    if (code && state) {
      // Limpar a URL imediatamente para não reprocessar em re-renders
      window.history.replaceState({}, document.title, window.location.pathname);
      handleCallbackMutation.mutate({ integracaoId: Number(state), code });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleConectar(id: number) {
    try {
      const result = await utils.integracoes.getOAuthUrl.fetch({ id });
      if (result?.url) {
        window.open(result.url, "_blank");
      }
    } catch (e: any) {
      toast(`Erro ao obter URL de autorização: ${e.message}`);
    }
  }

  function handleCriar() {
    const emitenteId = isAdmin ? Number(novaEmitenteId) : (user?.emitenteId as number);
    if (!emitenteId) {
      toast("Selecione um emitente.");
      return;
    }
    createMutation.mutate({
      emitenteId,
      plataforma: novaPlataforma as "mercadolivre",
    });
  }

  // Mapa de emitenteId → nome para exibição na tabela
  const emitenteMap = (emitentes ?? []).reduce((acc: Record<number, string>, e: any) => {
    acc[e.id] = e.nomeFantasia || e.razaoSocial;
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Integrações</h1>
        <Button onClick={() => setModalNovaAberto(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Integração
        </Button>
      </div>

      {/* Filtro por emitente (somente admin) */}
      {isAdmin && (
        <div className="mb-4 flex items-center gap-3">
          <Label className="whitespace-nowrap">Filtrar por emitente:</Label>
          <Select
            value={emitenteIdFiltro ? String(emitenteIdFiltro) : "todos"}
            onValueChange={(v) => setEmitenteIdFiltro(v === "todos" ? undefined : Number(v))}
          >
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Todos os emitentes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os emitentes</SelectItem>
              {(emitentes ?? []).map((e: any) => (
                <SelectItem key={e.id} value={String(e.id)}>
                  {e.nomeFantasia || e.razaoSocial}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Tabela de integrações */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Plataforma</th>
              {isAdmin && <th className="text-left px-4 py-3 font-medium">Emitente</th>}
              <th className="text-left px-4 py-3 font-medium">App ID</th>
              <th className="text-left px-4 py-3 font-medium">Conta ML</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {(!integracoes || integracoes.length === 0) && (
              <tr>
                <td colSpan={isAdmin ? 6 : 5} className="px-4 py-8 text-center text-muted-foreground">
                  Nenhuma integração cadastrada.
                </td>
              </tr>
            )}
            {(integracoes ?? []).map((integ: any) => (
              <tr key={integ.id} className="border-t hover:bg-muted/20">
                <td className="px-4 py-3 capitalize">{integ.plataforma}</td>
                {isAdmin && (
                  <td className="px-4 py-3 text-sm">
                    {emitenteMap[integ.emitenteId] ?? integ.emitenteId}
                  </td>
                )}
                <td className="px-4 py-3 font-mono text-xs">{integ.appId}</td>
                <td className="px-4 py-3">{integ.mlNickname ?? <span className="text-muted-foreground">—</span>}</td>
                <td className="px-4 py-3">{statusBadge(integ.status)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {integ.status !== "connected" && integ.status !== "disabled" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleConectar(integ.id)}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Conectar
                      </Button>
                    )}
                    {integ.status === "connected" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleConectar(integ.id)}
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Reconectar
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          if (confirm("Desativar esta integração?")) {
                            deleteMutation.mutate({ id: integ.id });
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal: Nova Integração */}
      <Dialog open={modalNovaAberto} onOpenChange={setModalNovaAberto}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nova Integração</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Plataforma</Label>
              <Select value={novaPlataforma} onValueChange={setNovaPlataforma}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLATAFORMAS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div>
                <Label>Emitente</Label>
                <Select value={novaEmitenteId} onValueChange={setNovaEmitenteId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o emitente" />
                  </SelectTrigger>
                  <SelectContent>
                    {(emitentes ?? []).map((e: any) => (
                      <SelectItem key={e.id} value={String(e.id)}>
                        {e.nomeFantasia || e.razaoSocial}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalNovaAberto(false)}>Cancelar</Button>
            <Button onClick={handleCriar} disabled={createMutation.isPending}>
              {createMutation.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
